import { Component, ComponentType } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { setPlaylist } from '@App/store/actions/player';
import SoundEffect from '@App/components/model/SoundEffect';
import { SoundEffectPropType, SoundEffectData } from '@App/components/model/SoundEffectPropType';

interface Props {
  sounds: SoundEffectData[]
  loadPlaylist: (playlist: unknown) => void
  resetPlaylist: () => void
}

class SoundEffectList extends Component<Props> {
  componentDidMount() {
    this.loadPlaylist();
  }

  componentWillUnmount() {
    this.props.resetPlaylist();
  }

  componentDidUpdate(prevProps: Props) {
    const { sounds } = this.props;

    // Sounds have changed
    if (JSON.stringify(sounds) !== JSON.stringify(prevProps.sounds)) {
      this.loadPlaylist();
    }
  }

  loadPlaylist() {
    const { loadPlaylist, sounds } = this.props;

    // Each entry carries the full sound-effect payload so the player can advance
    // (next/prev) without a server round-trip. `versionReference` stays null: the unique
    // `id` (mapped to trackReference) is enough to locate the entry in the playlist.
    const playlist = sounds.map(sound => ({
      trackReference: sound.id,
      versionReference: null,
      soundEffect: {
        id: sound.id,
        previewUrl: sound.previewUrl,
        title: sound.title,
        duration: sound.duration,
        waveformDataUrl: sound.waveformDataUrl,
        artworkUrl: sound.artworkUrl,
        // Needed by the player bar's SoundEffectButtonBar (reads sound.downloadUrl); playlist
        // entries are hand-built, not GraphQL nodes, so it must be copied through explicitly.
        downloadUrl: sound.downloadUrl,
      },
    }));

    loadPlaylist(playlist);
  }

  render() {
    const { sounds } = this.props;

    return (
      <div key="sound-effect-list" id="sound-effect-list">
        {sounds.map((sound, position) => <SoundEffect
          key={sound.id}
          sound={sound}
          searchResultsPosition={position + 1}
        />)}
      </div>
    );
  }
}

const Wrapper = connect(
  null,
  dispatch => ({
    loadPlaylist: playlist => dispatch(setPlaylist(playlist)),
    resetPlaylist: () => dispatch(setPlaylist()),
  }),
)(SoundEffectList);

// The `.propTypes` are attached to runtime component values that TypeScript's react/react-redux
// typings do not expose a writable `propTypes` on (a connected exotic component and a bare class);
// the cast keeps the exact runtime assignment while satisfying the compiler.
(Wrapper as unknown as ComponentType).propTypes = {
  sounds: PropTypes.arrayOf(SoundEffectPropType.isRequired).isRequired,
};

(SoundEffectList as unknown as ComponentType).propTypes = {
  ...(Wrapper as unknown as ComponentType).propTypes,
  loadPlaylist: PropTypes.func.isRequired,
  resetPlaylist: PropTypes.func.isRequired,
};

export default Wrapper;
